import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import ErrorBoundary from "@/components/ErrorBoundary";
import { ThemeProvider } from "@/context/ThemeContext";

// Pages
import AuthPage from "@/pages/AuthPage";
import Dashboard from "@/pages/Dashboard";
import VenuesPage from "@/pages/VenuesPage";
import VenueDetail from "@/pages/VenueDetail";
import MatchDetail from "@/pages/MatchDetail";
import ProfilePage from "@/pages/ProfilePage";
import VenueAdminDashboard from "@/pages/VenueAdminDashboard";
import VenueOwnerDashboard from "@/pages/VenueOwnerDashboard";
import OpenPositionsPage from "@/pages/OpenPositionsPage";
import CaptainDashboard from "@/pages/CaptainDashboard";
import PlayerPool from "@/pages/PlayerPool";
import ApplicationManagement from "@/pages/ApplicationManagement";
import CreateMatch from "@/pages/CreateMatch";
import VenueSettings from "@/pages/VenueSettings";
import AnnouncementManager from "@/pages/AnnouncementManager";
import AnnouncementsPage from "@/pages/AnnouncementsPage";
import ReservationCalendar from "@/pages/ReservationCalendar";
import DynamicPricing from "@/pages/DynamicPricing";
import RevenueAnalytics from "@/pages/RevenueAnalytics";
import VenueOwnerProfile from "@/pages/VenueOwnerProfile";
import AdminPanel from "@/pages/AdminPanel";
import AdminProfile from "@/pages/AdminProfile";
// Club System Pages
import CreditsPage from "@/pages/CreditsPage";
import TitlesPage from "@/pages/TitlesPage";
import ClubsPage from "@/pages/ClubsPage";
import CreateClubPage from "@/pages/CreateClubPage";
import ClubDetailPage from "@/pages/ClubDetailPage";
import ClubManagePage from "@/pages/ClubManagePage";
import RivalAdsPage from "@/pages/RivalAdsPage";
import SocialFeedPage from "@/pages/SocialFeedPage";
import ConnectionsPage from "@/pages/ConnectionsPage";
// Messaging & Social Pages
import MessagesPage from "@/pages/MessagesPage";
import ClubChatPage from "@/pages/ClubChatPage";
import RankingsPage from "@/pages/RankingsPage";
import MatchesPage from "@/pages/MatchesPage";
import HeadBallPage from "@/pages/HeadBallPage";
import TournamentsPage from "@/pages/TournamentsPage";
import TournamentDetailPage from "@/pages/TournamentDetailPage";
import AdminTournamentsPage from "@/pages/AdminTournamentsPage";
import AdminVenuesPage from "@/pages/AdminVenuesPage";
import AdminReportsPage from "@/pages/AdminReportsPage";
import ClaimVenuePage from "@/pages/ClaimVenuePage";
import VenueStoriesPage from "@/pages/VenueStoriesPage";
import NotFoundPage from "@/pages/NotFoundPage";
import SettingsPage from "@/pages/SettingsPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Axios interceptor for auth token
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem("token");
  return token ? children : <Navigate to="/auth" />;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching user:", error);
      localStorage.removeItem("token");
    } finally {
      setLoading(false);
    }
  };

  // Kullanıcı bilgisini yenileme fonksiyonu (kredi güncellemeleri için)
  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error("Error refreshing user:", error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    setUser(null);
    toast.success("Çıkış yapıldı");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-emerald-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <ThemeProvider>
    <div className="App">
      <BrowserRouter>
        <ErrorBoundary>
          <Routes>
          <Route path="/auth" element={<AuthPage setUser={setUser} />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Dashboard user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/venues"
            element={
              <ProtectedRoute>
                <VenuesPage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/venues/:id"
            element={
              <ProtectedRoute>
                <VenueDetail user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/matches/:id"
            element={
              <ProtectedRoute>
                <MatchDetail user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile/:id"
            element={
              <ProtectedRoute>
                <ProfilePage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          {/* Club System Routes */}
          <Route
            path="/credits"
            element={
              <ProtectedRoute>
                <CreditsPage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/titles"
            element={
              <ProtectedRoute>
                <TitlesPage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clubs"
            element={
              <ProtectedRoute>
                <ClubsPage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clubs/create"
            element={
              <ProtectedRoute>
                <CreateClubPage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clubs/:clubId"
            element={
              <ProtectedRoute>
                <ClubDetailPage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clubs/:clubId/manage"
            element={
              <ProtectedRoute>
                <ClubManagePage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/social"
            element={
              <ProtectedRoute>
                <SocialFeedPage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/connections"
            element={
              <ProtectedRoute>
                <ConnectionsPage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/messages"
            element={
              <ProtectedRoute>
                <MessagesPage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/messages/:userId"
            element={
              <ProtectedRoute>
                <MessagesPage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/club-chat"
            element={
              <ProtectedRoute>
                <ClubChatPage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rankings"
            element={
              <ProtectedRoute>
                <RankingsPage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/matches"
            element={
              <ProtectedRoute>
                <MatchesPage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/headball"
            element={
              <ProtectedRoute>
                <HeadBallPage user={user} onLogout={handleLogout} refreshUser={refreshUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rival-ads"
            element={
              <ProtectedRoute>
                <RivalAdsPage user={user} onLogout={handleLogout} refreshUser={refreshUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tournaments"
            element={
              <ProtectedRoute>
                <TournamentsPage user={user} onLogout={handleLogout} refreshUser={refreshUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tournaments/:tournamentId"
            element={
              <ProtectedRoute>
                <TournamentDetailPage user={user} onLogout={handleLogout} refreshUser={refreshUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/venue-admin"
            element={
              <ProtectedRoute>
                <VenueAdminDashboard user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/venue-owner-dashboard"
            element={
              <ProtectedRoute>
                <VenueOwnerDashboard user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/open-positions"
            element={
              <ProtectedRoute>
                <OpenPositionsPage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/captain-dashboard"
            element={
              <ProtectedRoute>
                <CaptainDashboard user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/player-pool"
            element={
              <ProtectedRoute>
                <PlayerPool user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/captain/applications"
            element={
              <ProtectedRoute>
                <ApplicationManagement user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/create-match"
            element={
              <ProtectedRoute>
                <CreateMatch user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/venue-settings/:venueId"
            element={
              <ProtectedRoute>
                <VenueSettings user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/announcements-manager"
            element={
              <ProtectedRoute>
                <AnnouncementManager user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/announcements"
            element={
              <ProtectedRoute>
                <AnnouncementsPage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reservation-calendar"
            element={
              <ProtectedRoute>
                <ReservationCalendar user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dynamic-pricing"
            element={
              <ProtectedRoute>
                <DynamicPricing user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/revenue-analytics"
            element={
              <ProtectedRoute>
                <RevenueAnalytics user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/venue-owner-profile"
            element={
              <ProtectedRoute>
                <VenueOwnerProfile user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminPanel user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-profile"
            element={
              <ProtectedRoute>
                <AdminProfile user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/tournaments"
            element={
              <ProtectedRoute>
                <AdminTournamentsPage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/venues"
            element={
              <ProtectedRoute>
                <AdminVenuesPage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/reports"
            element={
              <ProtectedRoute>
                <AdminReportsPage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/claim-venue"
            element={
              <ProtectedRoute>
                <ClaimVenuePage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/venue/:venueId/stories"
            element={
              <ProtectedRoute>
                <VenueStoriesPage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </ErrorBoundary>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
    </ThemeProvider>
  );
}

export default App;